import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Zap } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useQuickMatterMutations } from '../hooks';

interface QuickAdviceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Case 360° (Lot 2) Stage 4 — Quick Advice 3-click creation.
 * Only Topic + Contract + Responsible Partner are required. Everything else is
 * defaulted server-side (category = QA, phase = EXECUTION, 7-day deadline).
 */
export function QuickAdviceDialog({
  open,
  onOpenChange,
  onSuccess,
}: QuickAdviceDialogProps) {
  const { t } = useTranslation();
  const [topic, setTopic] = React.useState('');
  const [clientId, setClientId] = React.useState<number | undefined>(undefined);
  const [contractId, setContractId] = React.useState<number | undefined>(undefined);
  const [partnerId, setPartnerId] = React.useState<number | undefined>(undefined);
  const { createQuickMutation } = useQuickMatterMutations();

  React.useEffect(() => {
    if (open) {
      setTopic('');
      setClientId(undefined);
      setContractId(undefined);
      setPartnerId(undefined);
    }
  }, [open]);

  const canSubmit =
    topic.trim().length > 0 && !!contractId && !!partnerId && !createQuickMutation.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || !contractId || !partnerId) return;
    await createQuickMutation.mutateAsync({
      topic: topic.trim(),
      contractId,
      responsiblePartnerId: partnerId,
    });
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-500" />
            {t('quickAdvice.dialogTitle')}
          </DialogTitle>
          <DialogDescription>{t('quickAdvice.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('quickAdvice.topicLabel')}
            </label>
            <Textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t('quickAdvice.topicPlaceholder')}
              maxLength={500}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('selectClient')}
            </label>
            <UniversalCombobox
              url="/clients"
              labelKeys={['lastname', 'name', 'middlename']}
              valueKey="id"
              selectedId={clientId}
              onSelectItem={(item) => {
                setClientId(item?.['id'] as number | undefined);
                // A contract belongs to one client — clear it when the client changes.
                setContractId(undefined);
              }}
              placeholder={t('selectClient')}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('contractId')}
            </label>
            <UniversalCombobox
              url="/contracts"
              listUrl="/contracts/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={contractId}
              params={clientId ? { clientId } : {}}
              onSelectItem={(item) => setContractId(item?.['id'] as number | undefined)}
              placeholder={t('selectContractId')}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('quickAdvice.partnerLabel')}
            </label>
            <UniversalCombobox
              url="/users"
              listUrl="/users/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={partnerId}
              params={{ status: 'ACTIVE', role_type: 'PARTNER' }}
              onSelectItem={(item) => setPartnerId(item?.['id'] as number | undefined)}
              placeholder={t('quickAdvice.selectPartner')}
            />
          </div>

          <p className="text-xs text-slate-400">{t('quickAdvice.deadlineHint')}</p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createQuickMutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createQuickMutation.isPending ? t('processing') : t('quickAdvice.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
